import { useState, useEffect, useCallback } from "react";
import { Copy, Link2, Plus, Trash2, MousePointerClick, UserPlus, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const linkFor = (code) => `${window.location.origin}/register?ref=${code}`;

const RefCard = ({ r, onDelete }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(linkFor(r.code)); toast.success("Link copied"); }
    catch { toast.error("Couldn't access clipboard — copy the link manually"); }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid={`referral-card-${r.code}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{r.label || "Referral link"} {r.campaign && <span className="text-xs text-slate-400 font-normal">· {r.campaign}</span>}</p>
          <p className="text-xs text-slate-500">Promo <span className="font-mono font-semibold">REF-{r.code}</span> · {r.discount_percent}% off</p>
        </div>
        <button onClick={() => onDelete(r.id)} className="text-red-500 hover:text-red-700" data-testid={`referral-delete-${r.code}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2 mb-3">
        <Input readOnly value={linkFor(r.code)} className="text-xs bg-slate-50 font-mono" data-testid={`referral-url-${r.code}`} />
        <button onClick={copy} className="border border-slate-300 hover:bg-slate-50 px-3 rounded-lg" data-testid={`referral-copy-${r.code}`}><Copy className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[[MousePointerClick, "Clicks", r.clicks || 0], [UserPlus, "Signups", r.signups ?? r.signups_count ?? 0], [Link2, "Deals", r.deals_count || 0], [DollarSign, "Earned", `$${(r.total_commission || 0).toLocaleString()}`]].map(([Icon, label, val]) => (
          <div key={label} className="bg-slate-50 rounded-lg py-2" data-testid={`referral-${label.toLowerCase()}-${r.code}`}>
            <Icon className="w-3.5 h-3.5 mx-auto text-slate-400" /><p className="font-bold text-slate-900 text-sm">{val}</p><p className="text-[10px] uppercase text-slate-400">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ReferralLinksTab = () => {
  const [refs, setRefs] = useState([]);
  const [form, setForm] = useState({ label: "", campaign: "", discount_percent: 10 });

  const load = useCallback(() => api.get("/api/sales/referrals").then(r => setRefs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post("/api/sales/referrals", { ...form, discount_percent: Number(form.discount_percent) }); toast.success("Referral link created"); setForm({ label: "", campaign: "", discount_percent: 10 }); load(); }
    catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this referral link? Its promo code will stop working.")) return;
    try { await api.delete(`/api/sales/referrals/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="referral-links-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Create Referral Link</h2>
        <p className="text-sm text-slate-500 mb-4">Shops that sign up through your link get the discount automatically, and paid plans credit you a commission.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-sm">Label</Label><Input placeholder="Facebook ad" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} data-testid="referral-label-input" /></div>
          <div><Label className="text-sm">Campaign</Label><Input placeholder="spring-2026" value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} data-testid="referral-campaign-input" /></div>
          <div><Label className="text-sm">Discount %</Label><Input type="number" min="0" max="100" value={form.discount_percent} onChange={e => setForm({ ...form, discount_percent: e.target.value })} data-testid="referral-discount-input" /></div>
          <button onClick={create} className="bg-blue-600 hover:bg-blue-500 text-white font-medium py-2 rounded-lg flex items-center justify-center gap-1.5" data-testid="referral-create-btn"><Plus className="w-4 h-4" />Generate Link</button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {refs.map(r => <RefCard key={r.id} r={r} onDelete={remove} />)}
      </div>
      {refs.length === 0 && <p className="text-center text-slate-400 py-6 bg-white border border-slate-200 rounded-xl" data-testid="referral-empty">No referral links yet</p>}
    </div>
  );
};
